import React from "react";
import { connect } from "react-redux";
import {
    Redirect,
    Route,
    RouteComponentProps,
    RouteProps,
    withRouter
} from "react-router";
import { bindActionCreators } from "redux";
import Constants from "../../models/utility/Constants";
import { PermissionType } from "../../providers/SecurityProvider";
import { IAppState } from "../../store";
import * as UserStore from "../../store/UserStore";

type AuthRouteProps = RouteProps &
    RouteComponentProps &
    UserStore.IUserStoreState &
    typeof UserStore.actionCreators & {
        permission?: PermissionType;
    };

class AuthRoute extends React.PureComponent<AuthRouteProps> {
    public componentDidMount() {
        this.syncReturnLocation();
    }

    public componentDidUpdate() {
        this.syncReturnLocation();
    }

    public render() {
        // check login and permission (if passed)
        if (this.isAccessBlocked()) {
            return <Redirect to={Constants.Routes.Login()} />;
        }

        return <Route {...this.props} />;
    }

    private isAccessBlocked(): boolean {
        return (
            !this.props.user ||
            (!!this.props.permission && !this.props.permission(this.props.user))
        );
    }

    // Record the path while unauthenticated so Login/Register can return here,
    // but clear it again once the user has landed back on that path: otherwise
    // any later authenticated visit to /login (or a Profile/Register
    // post-success redirect) would bounce them to a stale prevLocation. Also
    // clear it when an authenticated user is blocked, so Login doesn't send
    // them back here and loop. (Login falls through to /sales once prevLocation
    // is empty.) Every dispatch is guarded because the connect below uses
    // { pure: false }, so react-redux re-renders this on every dispatch —
    // without the comparison the dispatch would repeat each time.
    private syncReturnLocation() {
        if (!this.props.location) {
            return;
        }
        const currentLocation =
            this.props.location.pathname +
            this.props.location.search +
            this.props.location.hash;

        if (!this.props.user) {
            if (this.props.prevLocation !== currentLocation) {
                this.props.updateLocation(currentLocation);
            }
        } else if (this.isAccessBlocked()) {
            if (this.props.prevLocation) {
                this.props.updateLocation("");
            }
        } else if (this.props.prevLocation === currentLocation) {
            this.props.updateLocation("");
        }
    }
}

export default withRouter(
    connect(
        (state: IAppState) => state.userStoreState,
        (dispatch) => bindActionCreators(UserStore.actionCreators, dispatch),
        null,
        { pure: false }
    )(AuthRoute)
);
